import { Button } from "@/components/ui/button"
import { SheetPageBody, SheetPageTitle } from "@/components/ui/sheet-page"
import { VALIDATION_ERROR_KEYS } from "@/i18n"
import { MailPlus } from "lucide-react"
import { useTranslation } from "react-i18next"
import * as z from "zod"
import { useNewProps } from "./page-props"
import { useController, useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { FormField } from "@/components/ui/form-field"
import { Combobox } from "@/components/ui/combobox"
import { Textarea } from "@/components/ui/textarea"
import { CommandItem } from "@/components/ui/command"
import { useNavigate } from "@/lib/page-rendering"

const validator = z.object({
  learners_relief_listing_id: z
    .string(VALIDATION_ERROR_KEYS.REQUIRED)
    .min(1, VALIDATION_ERROR_KEYS.REQUIRED),
  message: z.string().min(1, VALIDATION_ERROR_KEYS.REQUIRED),
})
type FormSchema = z.infer<typeof validator>

export default function Page() {
  const navigate = useNavigate()
  const { t } = useTranslation("school_relief_listings", {
    keyPrefix: "invitations",
  })
  const {
    listing_profile_name,
    relief_listing_options,
    relief_invitable_token,
  } = useNewProps()

  const {
    handleSubmit,
    control,
    register,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(validator),
    defaultValues: { message: t("form.message_pro_forma") },
  })
  const { field } = useController({
    name: "learners_relief_listing_id",
    control,
  })

  const onSubmit = (data: FormSchema) => {
    const { message, learners_relief_listing_id } = data

    navigate({
      href: ".",
      method: "post",
      data: {
        invitation: {
          learners_relief_listing_id,
        },
        message,
        relief_invitable_token,
      },
    }).then(({ errors }) => {
      if (Object.keys(errors).length > 0) {
        window.parent.postMessage("sheet:reload_on_close")
        window.parent.postMessage("sheet:close")
      }
    })
  }

  return (
    <SheetPageBody>
      <SheetPageTitle icon={MailPlus}>
        {t("new.title", { name: listing_profile_name })}
      </SheetPageTitle>
      <p className="mb-6">{t("new.description")}</p>
      <form
        role="form"
        onSubmit={handleSubmit(onSubmit)}
        className="flex h-full flex-col gap-5"
      >
        <FormField
          control={control}
          name={"learners_relief_listing_id"}
          label={t("form.listing")}
          errors={errors}
        >
          <Combobox
            value={field.value}
            onChange={(value) => field.onChange(value)}
            options={relief_listing_options}
            renderComboboxItem={({ key, setOpen, option }) => (
              <CommandItem
                className="flex-col items-start gap-0"
                key={option.value}
                disabled={option.disabled}
                keywords={[key, option.value?.toString() ?? ""]}
                aria-label={option.label}
                onSelect={() => {
                  if (field.value === option.value) {
                    field.onChange(null)
                  } else {
                    field.onChange(option.value)
                  }
                  setOpen(false)
                }}
              >
                <span>{option.label}</span>
                {option.disabled && (
                  <span className="text-xs">{t("form.disabled_listing")}</span>
                )}
              </CommandItem>
            )}
          />
        </FormField>
        <FormField
          {...register("message")}
          label={t("form.message")}
          errors={errors}
        >
          <Textarea />
        </FormField>
        <Button type="submit" className="mt-4 w-full">
          {t("send")}
        </Button>
      </form>
    </SheetPageBody>
  )
}
